import { useEffect, useMemo, useState } from "react";
import type { Feature, FeatureCollection, Polygon, MultiPolygon, Position } from "geojson";

export interface CountyEntry {
  name: string;          // e.g. "Sonoma County"
  geoid: string;         // e.g. "06097"
  bounds: [[number, number], [number, number]];  // [[south, west], [north, east]]
  centroid: [number, number];
}

interface CountyProps {
  county_geoid: string;
  county: string | null;
}

function walkCoords(coords: Position[][] | Position[][][]): { minLat: number; minLon: number; maxLat: number; maxLon: number } {
  let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
  const visit = (lon: number, lat: number) => {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  };
  const walk = (arr: unknown): void => {
    if (!Array.isArray(arr)) return;
    if (typeof arr[0] === "number") {
      visit(arr[0] as number, arr[1] as number);
      return;
    }
    arr.forEach(walk);
  };
  walk(coords);
  return { minLat, maxLat, minLon, maxLon };
}

function entryFromFeature(f: Feature<Polygon | MultiPolygon, CountyProps>): CountyEntry | null {
  const props = f.properties;
  if (!props.county || !props.county_geoid) return null;
  const { minLat, maxLat, minLon, maxLon } = walkCoords(
    f.geometry.coordinates as Position[][] | Position[][][],
  );
  return {
    name: props.county,
    geoid: props.county_geoid,
    bounds: [[minLat, minLon], [maxLat, maxLon]],
    centroid: [(minLat + maxLat) / 2, (minLon + maxLon) / 2],
  };
}

// Simple module-level cache so multiple consumers share one fetch.
let cache: CountyEntry[] | null = null;
let inFlight: Promise<CountyEntry[]> | null = null;

function loadCounties(): Promise<CountyEntry[]> {
  if (cache) return Promise.resolve(cache);
  if (inFlight) return inFlight;
  inFlight = fetch("/counties.geojson")
    .then((r) => (r.ok ? r.json() : null))
    .then((d: FeatureCollection<Polygon | MultiPolygon, CountyProps> | null) => {
      if (!d) return [];
      const list = d.features
        .map(entryFromFeature)
        .filter((x): x is CountyEntry => x !== null)
        .sort((a, b) => a.name.localeCompare(b.name));
      cache = list;
      return list;
    })
    .catch(() => [] as CountyEntry[])
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

export function useCounties() {
  const [counties, setCounties] = useState<CountyEntry[]>(cache ?? []);
  useEffect(() => {
    if (cache) return;
    let alive = true;
    loadCounties().then((list) => {
      if (alive) setCounties(list);
    });
    return () => {
      alive = false;
    };
  }, []);
  return counties;
}

export function useCountyByGeoid(geoid: string | null): CountyEntry | null {
  const all = useCounties();
  return useMemo(() => all.find((c) => c.geoid === geoid) ?? null, [all, geoid]);
}
